/** Searchable language picker, for the UI language and for a session's
 *  output language.
 *
 *  An MUI ``Autocomplete`` over the registry: each language shows its
 *  own name (endonym) first and its name in the reader's language
 *  under it, search is case- and accent-insensitive across both names,
 *  the English name, the code and the registry keywords, and an empty
 *  search shows a Suggested group (current value, browser languages,
 *  and for output the UI language) above All languages. Presentational:
 *  the caller owns the value and what a pick does. */
import { Check as CheckIcon, Search as SearchIcon } from "@/components/icons";
import * as React from "react";
import {
  Autocomplete,
  Box,
  InputAdornment,
  ListSubheader,
  TextField,
  Typography,
  type PopperProps,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { intlLocale } from "@/i18n/format";
import { entry, languages as registryLanguages, type LanguageEntry } from "@/i18n/languages";

import {
  browserLanguages,
  buildOptions,
  normalizeSearch,
  searchOptions,
  suggestedCodes,
  type LanguageOption,
  type PickerKind,
} from "./languageOptions";

export interface LanguagePickerProps {
  kind: PickerKind;
  /** The selected code; null selects the "follow UI" option. */
  value: string | null;
  onChange: (code: string | null) => void;
  /** ``field``: a text field that drops a list down. ``list``: the
   *  search field with the list always open under it, filling its
   *  container (the app-bar popover and the phone dialog). */
  variant: "field" | "list";
  /** Output only: offer a pinned option that clears the value. */
  followUi?: { label: string };
  label?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
  /** ``list``: Escape was pressed; the container should close. */
  onClose?: () => void;
  /** ``list``: rows at least 48 px high (touch targets on a phone). */
  touch?: boolean;
  /** ``list``: the listbox's maximum height (default: fill the container). */
  listMaxHeight?: string | number;
  /** The registry entries to offer (tests pass a fixture). */
  languages?: readonly LanguageEntry[];
  /** The browser's languages for Suggested (tests pass a fixture). */
  navigatorLanguages?: readonly string[];
}

const FOLLOW_KEY = "follow";

interface Row {
  key: string;
  /** "" in a flat search result (no group header). */
  group: string;
  /** null: the "follow UI" option. */
  option: LanguageOption | null;
}

/** The popper slot for the ``list`` variant: render the listbox in
 *  place under the search field instead of in a floating popper. */
function InlinePopper(props: PopperProps): React.JSX.Element {
  const { disablePortal: _p, anchorEl: _a, open: _o, style: _s, ...rest } = props;
  return <Box {...(rest as React.ComponentProps<typeof Box>)} />;
}

function adHocOption(code: string, list: readonly LanguageEntry[]): LanguageOption {
  const e = list.find((l) => l.code === code) ?? entry(code);
  return {
    code,
    endonym: e?.endonym ?? code,
    englishName: e?.englishName ?? code,
    localizedName: e?.englishName ?? code,
    short: e?.short ?? code.toUpperCase(),
    dir: e?.dir ?? "ltr",
    terms: [code],
  };
}

export function LanguagePicker({
  kind,
  value,
  onChange,
  variant,
  followUi,
  label,
  helperText,
  disabled,
  onClose,
  touch,
  listMaxHeight,
  languages = registryLanguages,
  navigatorLanguages,
}: LanguagePickerProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const isList = variant === "list";
  // Names in the reader's language go through Intl, so they take the
  // Intl locale (regional tag, registry extensions), not the bare code.
  const displayLocale = intlLocale(i18n.language, navigatorLanguages ?? browserLanguages());
  const [input, setInput] = React.useState("");
  const pickerId = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);

  // The list variant opens inside a popover or dialog whose focus trap
  // can take focus back to its own root after mount (it does under
  // StrictMode's effect replay), so focus the search box once the
  // container has settled, not only through ``autoFocus``.
  React.useEffect(() => {
    if (!isList) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [isList]);

  const options = React.useMemo(
    () => buildOptions(kind, displayLocale, languages),
    [kind, displayLocale, languages],
  );

  const suggestedLabel = t("language_picker.suggested");
  const allLabel = t("language_picker.all");

  const { rows, selected, followRow } = React.useMemo(() => {
    const byCode = new Map(options.map((o) => [o.code, o]));
    const follow: Row | null = followUi ? { key: FOLLOW_KEY, group: suggestedLabel, option: null } : null;
    const suggested = suggestedCodes({
      value,
      navigatorLanguages: navigatorLanguages ?? browserLanguages(),
      uiLanguage: i18n.resolvedLanguage ?? i18n.language,
      kind,
      allowed: options.map((o) => o.code),
      list: languages,
    });
    const out: Row[] = [];
    if (follow) out.push(follow);
    // A value the picker no longer offers (a stale session language)
    // still shows as selected rather than as a blank field.
    let current: Row | null = null;
    if (value !== null && !byCode.has(value)) {
      current = { key: `s:${value}`, group: suggestedLabel, option: adHocOption(value, languages) };
      out.push(current);
    }
    for (const code of suggested) {
      out.push({ key: `s:${code}`, group: suggestedLabel, option: byCode.get(code)! });
    }
    for (const option of options) {
      const row: Row = { key: `a:${option.code}`, group: allLabel, option };
      if (option.code === value) current = row;
      out.push(row);
    }
    return { rows: out, selected: value === null ? follow : current, followRow: follow };
  }, [options, value, followUi, suggestedLabel, allLabel, navigatorLanguages, i18n, kind, languages]);

  const labelOf = (row: Row): string => row.option?.endonym ?? followUi?.label ?? "";

  const filterOptions = (all: Row[], state: { inputValue: string }): Row[] => {
    const query = normalizeSearch(state.inputValue);
    if (!query) return all;
    const pool = all.filter((r) => r.key.startsWith("a:")).map((r) => r.option!);
    const hits: Row[] = searchOptions(pool, query).map((option) => ({
      key: `a:${option.code}`,
      group: "",
      option,
    }));
    if (followRow && followUi && normalizeSearch(followUi.label).includes(query)) {
      hits.unshift({ ...followRow, group: "" });
    }
    return hits;
  };

  const renderRow = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    row: Row,
    state: { selected: boolean },
  ) => {
    const { key: _key, ...liProps } = props;
    const option = row.option;
    const secondary =
      option && normalizeSearch(option.localizedName) !== normalizeSearch(option.endonym)
        ? option.localizedName
        : null;
    return (
      <li key={row.key} {...liProps}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            minHeight: touch ? 48 : undefined,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {option ? (
              <Typography component="span" sx={{ display: "block" }} lang={option.code} dir={option.dir}>
                {option.endonym}
              </Typography>
            ) : (
              <Typography component="span" sx={{ display: "block" }}>
                {followUi?.label}
              </Typography>
            )}
            {secondary && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {secondary}
              </Typography>
            )}
          </Box>
          {state.selected && (
            <CheckIcon fontSize="small" color="primary" titleAccess={t("language_picker.current")} />
          )}
        </Box>
      </li>
    );
  };

  // listbox > group > option: MUI's default group markup (a plain
  // ``li`` holding a ``ul``) is a list inside a listbox, which axe
  // rightly rejects. The listbox has no top padding (see listProps): a
  // sticky header sticks below the scroller's padding, and the rows
  // scrolling through that gap showed above it.
  const renderGroup = (params: { key: React.Key; group: string; children?: React.ReactNode }) => {
    if (!params.group) return <React.Fragment key={params.key}>{params.children}</React.Fragment>;
    const headerId = `${pickerId}-group-${params.key}`;
    return (
      <li key={params.key} role="presentation">
        <ListSubheader id={headerId} component="div" role="presentation" sx={{ lineHeight: "32px" }}>
          {params.group}
        </ListSubheader>
        <Box component="ul" role="group" aria-labelledby={headerId} sx={{ p: 0 }}>
          {params.children}
        </Box>
      </li>
    );
  };

  const listProps = isList
    ? {
        // Always open, listbox rendered in place by InlinePopper (not
        // ``disablePortal``, whose popper is absolutely positioned).
        open: true,
        forcePopupIcon: false,
        // A search box, not a value display: MUI would reset the text
        // to the selected label (on a value change or a blur), so the
        // list variant controls it.
        inputValue: input,
        clearOnBlur: false,
        onClose: (_e: unknown, reason: string) => {
          if (reason === "escape") onClose?.();
        },
        slots: { popper: InlinePopper },
        slotProps: {
          paper: { elevation: 0, square: true, sx: { bgcolor: "transparent" } },
          listbox: {
            sx: { maxHeight: listMaxHeight ?? "none", overflow: "auto", pt: 0 },
          },
        },
      }
    : {
        openOnFocus: true,
        slotProps: { listbox: { sx: { pt: 0 } } },
      };

  return (
    <Autocomplete<Row, false, true, false>
      {...listProps}
      onInputChange={(_e, v, reason) => {
        if (!(isList && (reason === "reset" || reason === "blur"))) setInput(v);
      }}
      options={rows}
      value={selected ?? undefined}
      onChange={(_e, row) => {
        if (row) onChange(row.option?.code ?? null);
      }}
      disableClearable
      autoHighlight
      disabled={disabled}
      groupBy={(row) => row.group}
      renderGroup={renderGroup}
      filterOptions={filterOptions}
      getOptionLabel={labelOf}
      getOptionKey={(row) => row.key}
      isOptionEqualToValue={(a, b) => (a.option?.code ?? null) === (b.option?.code ?? null)}
      renderOption={renderRow}
      noOptionsText={t("language_picker.no_match", { query: input.trim() })}
      renderInput={(params) => (
        <TextField
          {...params}
          label={isList ? undefined : label}
          helperText={helperText}
          size="small"
          autoFocus={isList}
          inputRef={inputRef}
          placeholder={isList ? t("language_picker.search_placeholder") : undefined}
          slotProps={{
            ...params.slotProps,
            htmlInput: {
              ...params.slotProps.htmlInput,
              ...(isList || !label ? { "aria-label": t("common.language") } : {}),
            },
            input: {
              ...params.slotProps.input,
              ...(isList
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }
                : {}),
            },
          }}
        />
      )}
    />
  );
}
